import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store";
import {
  FetchFiltersAndAuctionDataAction,
  IApplicationState,
  IFilterItem,
  UpateAuctionCorridorFilterValueAction,
  UpateAuctionInterconnectorFilterValueAction,
  UpateAuctionIsShowFilterValueAction,
} from "../../store";
import { getAuctionTimezone, nameof } from "../../core";
import {
  BidMessageEnum,
  BidStatus,
  FeatureHeadings,
  IAuction,
  IBook,
  IProfile,
} from "../../services";
import {
  DefaultLayout,
  GridCheckboxFilterPanel,
  ICheckBoxFilter,
  ICheckBoxValueEvent,
  BtnCellRenderer,
  ToggleRadio,
  GridDateTimeRenderer,
  Calendar,
} from "../../components";
import { useTheme } from "styled-components";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
} from "ag-grid-community";
import moment from "moment-timezone";
import Grid from "../../components/Grid/Grid";
import { Dispatch } from "redux";
import {
  IUpateAuctionBidStatusFilterValueAction,
  IUpdateAuctionCorridorFilterValueAction,
  IUpdateAuctionExchangeFilterValueAction,
  IUpdateAuctionInterconnectorFilterValueAction,
  IUpdateAuctionProductFilterValueAction,
} from "../../store/Auction";
import {
  UpateAuctionBidStatusFilterValueAction,
  UpateAuctionProductFilterValueAction,
  UpdateAuctionExchangeFilterValueAction,
} from "../../store/Auction/ActionCreator";
import { FetchBookAction } from "../../store/Book/ActionCreator";

import { product, showHide } from ".";
import AuctionBidStatusRenderer from "./AuctionBidStatusRenderer";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import AuctionResultStatusRenderer from "./AuctionResultStatusRenderer";
import { AuctionResultsRenderer } from "./AuctionResultsRendererer";

interface IAuctionProps {}

interface IAuctionPanelItem {
  options: string[];
  value: string;
  onShowChange: any;
}

export const allSelected = (data?: IFilterItem[]) => {
  if (!data || data.some((x) => !x.isSelected)) {
    return false;
  }
  return true;
};

export const buildGridCheckBoxFilter = (
  action: (
    currentCorridors: IFilterItem[],
    updateValue: IFilterItem
  ) =>
    | IUpdateAuctionCorridorFilterValueAction
    | IUpdateAuctionInterconnectorFilterValueAction
    | IUpdateAuctionProductFilterValueAction
    | IUpateAuctionBidStatusFilterValueAction
    | IUpdateAuctionExchangeFilterValueAction,
  name: string,
  data?: IFilterItem[],
  gridApi?: GridApi | null,
  dispatch?: Dispatch<any>
) => {

  if (data && data.length > 0) {
    const valueEvent = data.map((item: IFilterItem) => {
      return {
        value: item.value,
        isChecked: item.isSelected,
        color: item.color,
        event: (result: any) => {
          if (gridApi && data && dispatch) {
            const itemTarget = result?.target;
            dispatch(
              action(data, {
                value: itemTarget?.value,
                isSelected: itemTarget?.checked,
                color: item?.color,
              } as IFilterItem)
            );
          }
        },
      } as ICheckBoxValueEvent<string>;
    });

    const all = {
      value: "All",
      isChecked: allSelected(data),
      event: (result: any) => {
        if (gridApi && data && dispatch) {
          const item = result?.target;
          dispatch(
            action(data, {
              value: item?.value,
              isSelected: item?.checked,
            } as IFilterItem)
          );
        }
      },
    } as ICheckBoxValueEvent<string>;

    const filters = {
      columnName: name,
      valueEvent,
      all,
    } as ICheckBoxFilter<string>;
    return filters;
  }
};

export const filterByFilterItems = (
  node: IRowNode,
  filterName: string,
  filterItems?: IFilterItem[]
) => {
  const selected = filterItems?.filter((x) => x.isSelected).map((c) => c.value);
  return (
    selected !== undefined &&
    selected &&
    node.data.hasOwnProperty(filterName) &&
    selected.includes(node.data[filterName])
  );
};

  /**
   * A function that filters Auction data based on product type (daily, intraday, term) filters.
   * If all filters are selected, this function returns true.
   * If none of the filters are selected, this function returns false.
   * If some of the filters are selected, this function checks if the product type of the current node matches
   * any of the selected filter values. If it does, this function returns true. Otherwise, it returns false.
   * @param node The current node to be filtered
   * @param filterItemsArg An array of IFilterItem objects that represent the product type filters
   * @returns A boolean indicating whether the current node passes the filter
   */
export const filterByProduct = (node: IRowNode, filterItemsArg?: IFilterItem[]) => {
  const filterItems = filterItemsArg || [];
  const allSelected  = filterItems.length === filterItems.filter((x) => x.isSelected).length;

  if (allSelected ) {
    return true;
  }

  const isDaily =
    filterItems?.find(
      (x) =>
        x.isSelected === true &&
        x.value.toLowerCase() === product.daily.toLowerCase()
    ) && node.data.product.toLowerCase() === product.daily.toLowerCase();

  if (isDaily) {
    return true;
  }

  const isIntraday =
    filterItems?.find(
      (x) =>
        x.isSelected === true &&
        x.value.toLowerCase() === product.intraday.toLowerCase()
    ) && node.data.product.toLowerCase() === product.intraday.toLowerCase();

  if (isIntraday) {
    return true;
  }

  const isTerm =
    filterItems?.find(
      (x) =>
        x.isSelected === true &&
        x.value.toLowerCase() === product.term.toLowerCase()
    ) &&
    (node.data.product.toLowerCase() === product.quarterly.toLowerCase() ||
      node.data.product.toLowerCase() === product.seasonal.toLowerCase() ||
      node.data.product.toLowerCase() === product.yearly.toLowerCase() ||
      node.data.product.toLowerCase() === product.monthly.toLowerCase());

  if (isTerm) {
    return true;
  }

  const isWeekly =
    filterItems?.find(
      (x) =>
        x.isSelected === true &&
        x.value.toLowerCase() === product.weekly.toLowerCase()
    ) &&
    ( node.data.product.toLowerCase() === product.weekly.toLowerCase());

  if (isWeekly) {
    return true;
  }

  const isWeekend =
    filterItems?.find(
      (x) =>
        x.isSelected === true &&
        x.value.toLowerCase() === product.weekend.toLowerCase()
    ) &&
    ( node.data.product.toLowerCase() === product.weekend.toLowerCase());

  if (isWeekend) {
    return true;
  }

  return false;
};

export const isDefined = (value: unknown) => {
  return value !== null && value !== undefined;
};

const timezone = getAuctionTimezone();
const dateTimeFormatter = (params: ICellRendererParams) => {
  return isDefined(moment(params.value)?.tz(timezone))
    ? moment(params.value).tz(timezone).format("DD MMM YYYY - HH:mm:ss")
    : "";
}

type ColDefs = ColDef<any,any>[];

// grid
const auctionColumns = (profilePage : IProfile): ColDefs => {
  return [
    {
      field: nameof<IAuction>("exchangeCode"),
      sortable: true,
      flex: 0.8
    },
    { field: nameof<IAuction>("interconnector"), headerName: "IC", flex: 0.4 },
    { field: nameof<IAuction>("corridor"), flex: 0.7 },
    { field: nameof<IAuction>("product"), flex: 0.6 },
    {
      field: nameof<IAuction>("periodStart"),
      valueFormatter: dateTimeFormatter,
      flex: 1,
    },
    {
      field: nameof<IAuction>("periodStop"),
      headerName: "Period End",
      valueFormatter: dateTimeFormatter,
      flex: 1,
    },
    {
      field: nameof<IAuction>("biddingOpen"),
      cellRendererParams: GridDateTimeRenderer,
      valueFormatter: dateTimeFormatter,
      flex: 1,
    },
    {
      field: nameof<IAuction>("biddingClose"),
      cellRendererParams: GridDateTimeRenderer,
      valueFormatter: dateTimeFormatter,
      sort: "asc",
      flex: 1,
    },
    { 
      field: "Offered Capacity(MW)",
      cellRenderer: BtnCellRenderer,
      cellRendererParams: { 
        profilePage: profilePage
      },
      flex: 1,
    },
    {
      field: nameof<IAuction>("bidStatus"),
      flex: 0.65,
      cellRenderer: AuctionBidStatusRenderer,
      sortable: true,
    },
    {
      field: nameof<IAuction>("auctionResultStatus"),
      headerName: "Shell Results",
      cellRenderer: AuctionResultStatusRenderer,
      sortable: true,
      flex: 1.2,
    },
    {
      field: nameof<IAuction>("auctionResult"),
      headerName: "All Results",
      cellRenderer: AuctionResultsRenderer,
      sortable: true,
      flex: 1.2,
    }
  ] as ColDef[];
};

const Auction: React.FC<IAuctionProps> = () => {
  const dispatch = useAppDispatch();
  const isLoading = useSelector((state: IApplicationState) => state.auction.loading);
  const data = useSelector((state: IApplicationState) => state.auction.data);
  const filters = useSelector((state: IApplicationState) => state.auction.currentFilters);
  const exceptionMessage = useSelector((state: IApplicationState) => state.auction.exceptionMessage);
  const bidStatus = useSelector((state: IApplicationState) => state.bidPanel.status);
  const profilePage = useSelector((state: any) => state.profile.profile);

  const refFilters = useRef(filters);
  const [rowData, setRowData] = useState<IAuction[]>(data || []);
  const gridOptions: GridOptions = { rowHeight: 40, headerHeight: 36 };
  const theme = useTheme();
  const [day, setDay] = useState(moment(filters?.startDateUtc));
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const options = [showHide.show, showHide.hide];
  const [show, setShow] = useState(filters?.isShow !== false ? showHide.show : showHide.hide);
  const [alertMessage, setAlertMessage] = useState("");

  const books = useSelector((state: IApplicationState) => state.book.books);
  const getColumns: () => ColDefs = useCallback(() => { 
    return auctionColumns(profilePage) as ColDefs
  }, [profilePage]);

  useEffect(() => {
    if (!data) {
      dispatch(FetchFiltersAndAuctionDataAction(day.toDate()));
    }
  }, [data, dispatch, day]);

  useEffect(() => {
    setRowData(data || []);
  }, [data, gridApi]);

  useEffect(() => {
    if (gridApi) {
      refFilters.current = filters;
      gridApi.onFilterChanged();
    }
  }, [filters, gridApi]);

  useEffect(() => {
    if (exceptionMessage) {
      setAlertMessage(exceptionMessage);
    }
  }, [exceptionMessage]);

  useEffect(() => {
    if (bidStatus === BidStatus.DraftFailed) {
      setAlertMessage(BidMessageEnum.CannotSaveDraftBid);
    } else {
      setAlertMessage("");
    }
  }, [bidStatus]);

  useEffect(() => {
    if (!books || books.length === 0) {
      dispatch(FetchBookAction());
    }
  }, [books, dispatch]);

  useEffect(() => {
    if (books) {
      data?.forEach((x) => {
        x.books = books.map((y) => {
          return { code: y.code, name: y.name } as IBook;
        });
      });
    }
  }, [books, data]);

  const corridorCheckBoxFilter = buildGridCheckBoxFilter(
    UpateAuctionCorridorFilterValueAction,
    "Corridor".toUpperCase(),
    filters?.corridors,
    gridApi,
    dispatch
  );

  const productCheckBoxFilter = buildGridCheckBoxFilter(
    UpateAuctionProductFilterValueAction,
    "Product".toUpperCase(),
    filters?.products,
    gridApi,
    dispatch
  );

  const bidStatusCheckBoxFilter = buildGridCheckBoxFilter(
    UpateAuctionBidStatusFilterValueAction,
    "Bid Status".toUpperCase(),
    filters?.bidStatus,
    gridApi,
    dispatch
  );

  const icCheckBoxrFilter = buildGridCheckBoxFilter(
    UpateAuctionInterconnectorFilterValueAction,
    "IC".toUpperCase(),
    filters?.interconnectors,
    gridApi,
    dispatch
  );

  const exchangeCheckBoxFilter = buildGridCheckBoxFilter(
    UpdateAuctionExchangeFilterValueAction,
    "Exchange".toUpperCase(),
    filters?.exchanges,
    gridApi,
    dispatch
  );

  const applyShowFilter = (selected: showHide) => {
    dispatch(UpateAuctionIsShowFilterValueAction(selected === showHide.show));

    if (gridApi) {
      setTimeout(() => {
        gridApi.onFilterChanged();
      }, 0);
    }
  }

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    applyShowFilter(show);
  };

  const filterAuctionData = (node: IRowNode): boolean => {
    if (refFilters.current?.isShow !== false) {
      return (
        filterByFilterItems(node, nameof<IAuction>("exchangeCode"), refFilters.current?.exchanges) &&
        filterByFilterItems(node, "interconnector", refFilters.current?.interconnectors) &&
        filterByProduct(node, refFilters.current?.products) &&
        filterByFilterItems(node, nameof<IAuction>("bidStatus"), refFilters.current?.bidStatus)
      );
    }

    return (
      filterByFilterItems(node, "corridor", refFilters.current?.corridors) &&
      node.data["interconnector"] === "N/A" &&
      filterByProduct(node, refFilters.current?.products) &&
      filterByFilterItems(node, nameof<IAuction>("bidStatus"), refFilters.current?.bidStatus) &&
      filterByFilterItems(node, nameof<IAuction>("exchangeCode"), refFilters.current?.exchanges)
    );
  };

  const PanelItem: React.FC<IAuctionPanelItem> = (props) => {
    return (
      <div>
        <Calendar day={day} setDay={setDay} />
        <GridCheckboxFilterPanel
          data={exchangeCheckBoxFilter}
          idPrefix="exchange"
        ></GridCheckboxFilterPanel>
        <GridCheckboxFilterPanel
          data={productCheckBoxFilter}
          idPrefix="product"
        />
        <GridCheckboxFilterPanel
          data={bidStatusCheckBoxFilter}
          idPrefix="bidStatus"
        />
        <ToggleRadio 
          showOrHide={show === showHide.show ? "show" : "hide"} 
          onChange={onShowChange}
        />
        <GridCheckboxFilterPanel
          disabled={show !== showHide.show}
          data={icCheckBoxrFilter}
          idPrefix="interconnector"
        />
        <GridCheckboxFilterPanel
          disabled={show !== showHide.hide}
          data={corridorCheckBoxFilter}
          idPrefix="corridor"
        />
      </div>
    );
  };

  const onShowChange = () => {
    const updated =
      show === showHide.show
      ? showHide.hide
      : showHide.show;
      
    setShow(updated);
    applyShowFilter(updated);
  };

  const columns = getColumns();

  return (
    <div data-testid="Auction">
      <DefaultLayout
        panel={
          <PanelItem
            value={show}
            options={options}
            onShowChange={onShowChange}
          />
        }
        title={FeatureHeadings.Auctions}
        message={alertMessage}
        children={
          isLoading ? (
            <LoadingIndicator />
          ) : (
            <Grid
              columns={columns}
              rowData={rowData}
              theme={theme}
              onGridEvent={onGridReady}
              hasExternalFilter={true}
              filterFunction={filterAuctionData}
              gridOptions={gridOptions}
            />
          )
        }
      />
    </div>
  );
};

export default Auction;